/**
 * ImportClientsDialog — bulk-CSV client import.
 *
 * Two-step UX:
 *   1. Drop / pick a CSV file → POST /clients/import-preview → show parsed rows + errors + dupes
 *   2. Confirm → POST /clients/import → show created count + skipped list, then close + refresh
 *
 * Saves agencies hours when onboarding 10+ clients at once.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Upload, FileSpreadsheet, Loader2, Check, AlertTriangle, Download, ArrowLeft, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImportClientsDialog = ({ open, onOpenChange, onImported }) => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('pick'); // pick | preview | result
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setStep('pick');
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handlePickFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name) && f.type && !f.type.includes('csv')) {
      toast.error('Please pick a .csv file');
      return;
    }
    if (f.size > 200 * 1024) {
      toast.error('CSV must be under 200 KB');
      return;
    }
    setFile(f);
    runPreview(f);
  };

  const runPreview = async (f) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/clients/import-preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      setStep('preview');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed — invalid CSV?');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/clients/import?skip_duplicates=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      setStep('result');
      onImported?.();
      toast.success(`Imported ${r.data.created} client${r.data.created === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/clients/import-template`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token')}` },
      });
      const blob = new Blob([r.data.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'posttosocial-clients-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error('Could not download template');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-clients-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Import clients from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a spreadsheet with up to 200 rows. We'll validate every row + skip duplicates by name.
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-3">
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (e.dataTransfer.files?.[0]) handlePickFile(e.dataTransfer.files[0]);
              }}
              className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center cursor-pointer hover:bg-zinc-50 transition"
              data-testid="csv-dropzone"
            >
              {loading ? (
                <Loader2 className="w-8 h-8 mx-auto text-zinc-400 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                  <p className="font-medium text-sm">Drop your CSV here or click to browse</p>
                  <p className="text-xs text-zinc-500 mt-1">Max 200 KB · UTF-8 encoded</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handlePickFile(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
            <div className="text-xs text-zinc-500 space-y-1 bg-zinc-50 p-3 rounded">
              <p className="font-medium text-zinc-700">Expected columns:</p>
              <p><code>name</code> <span className="text-rose-500">(required)</span>, <code>brand_color</code> (#RRGGBB), <code>review_email</code>, <code>review_contact_name</code>, <code>notes</code></p>
            </div>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download sample template
            </Button>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <Badge variant="secondary" className="justify-center">{preview.rows.length} valid</Badge>
              <Badge className={`justify-center ${preview.errors.length ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-600'}`}>
                {preview.errors.length} error{preview.errors.length === 1 ? '' : 's'}
              </Badge>
              <Badge className={`justify-center ${(preview.duplicates_in_db.length + preview.duplicates_in_csv.length) ? 'bg-orange-100 text-orange-800' : 'bg-zinc-100 text-zinc-600'}`}>
                {preview.duplicates_in_db.length + preview.duplicates_in_csv.length} dupes
              </Badge>
            </div>

            <div className="border border-zinc-200 rounded-md max-h-72 overflow-y-auto">
              <table className="w-full text-xs" data-testid="preview-table">
                <thead className="sticky top-0 bg-zinc-50 z-10">
                  <tr>
                    <th className="text-left px-2 py-1.5">#</th>
                    <th className="text-left px-2 py-1.5">Name</th>
                    <th className="text-left px-2 py-1.5">Color</th>
                    <th className="text-left px-2 py-1.5">Review email</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const isDupDb = preview.duplicates_in_db.includes(r.name);
                    const isDupCsv = preview.duplicates_in_csv.includes(r.name);
                    return (
                      <tr key={r.row} className={`border-t border-zinc-100 ${isDupDb ? 'bg-orange-50' : ''}`}>
                        <td className="px-2 py-1.5 text-zinc-400">{r.row}</td>
                        <td className="px-2 py-1.5">
                          {r.name}
                          {isDupDb && <Badge className="ml-1 bg-orange-100 text-orange-700 text-[10px]">already exists</Badge>}
                          {isDupCsv && <Badge className="ml-1 bg-orange-100 text-orange-700 text-[10px]">dup in CSV</Badge>}
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex items-center gap-1">
                            <span
                              className="w-3 h-3 rounded-full border border-zinc-200"
                              style={{ background: r.brand_color || '#6366f1' }}
                            />
                            <span className="text-zinc-500">{r.brand_color || 'default'}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-zinc-500">{r.review_email || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {preview.errors.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                <p className="font-medium text-amber-900 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Errors will be skipped
                </p>
                {preview.errors.map((e, i) => (
                  <p key={i} className="text-amber-800">
                    Row {e.row} · <code>{e.field}</code>: {e.message}
                  </p>
                ))}
              </div>
            )}

            {preview.duplicates_in_db.length > 0 && (
              <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                {preview.duplicates_in_db.length} name{preview.duplicates_in_db.length === 1 ? '' : 's'} already exist as clients — they will be skipped: <b>{preview.duplicates_in_db.join(', ')}</b>
              </p>
            )}
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <Check className="w-5 h-5 text-emerald-600" />
                <p className="font-semibold text-emerald-900">
                  {result.created} client{result.created === 1 ? '' : 's'} imported
                </p>
              </div>
              {result.names.length > 0 && (
                <p className="text-xs text-emerald-800">{result.names.join(', ')}</p>
              )}
            </div>
            {result.skipped_duplicates.length > 0 && (
              <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                <b>{result.skipped_duplicates.length} skipped</b> (already existed): {result.skipped_duplicates.join(', ')}
              </p>
            )}
            {result.errors.length > 0 && (
              <p className="text-xs text-amber-700">{result.errors.length} row{result.errors.length === 1 ? '' : 's'} had errors and were skipped.</p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'pick' && (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={reset} data-testid="back-to-pick-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <Button
                onClick={handleImport}
                disabled={loading || preview.rows.length === 0}
                data-testid="confirm-import-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Import {preview.rows.length - preview.duplicates_in_db.length} client{preview.rows.length - preview.duplicates_in_db.length === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {step === 'result' && (
            <>
              <Button variant="outline" onClick={reset} data-testid="import-another-btn">Import another</Button>
              <Button onClick={() => onOpenChange(false)} data-testid="close-import-btn">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportClientsDialog;
